import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const is_date = date => {
  if (Object.prototype.toString.call(date) === "[object Date]") {
    return true;
  }
  return false;
};

const getTimeFromDate = date => {
  let dateObj = is_date(date) ? date : new Date(date);
  let hour = dateObj.getHours();
  let minute = dateObj.getMinutes();
  let meridian = "am";
  if (hour > 12) {
    hour -= 12;
    meridian = "pm";
  }
  if (hour === 0) {
    hour = 12;
  }
  if (minute < 10) {
    minute = "0" + minute;
  }
  return hour + ":" + minute + " " + meridian;
};

const CLOCK_FORMAT = {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZoneName: "short",
};

/**
 * Whether `name` is a usable IANA timezone.
 *
 * @param {string|null|undefined} name
 * @return {boolean}
 */
export const isValidTimeZone = name => {
  if (!name || typeof name !== "string") {
    return false;
  }
  try {
    Intl.DateTimeFormat("en", { timeZone: name }).format();
    return true;
  } catch (err) {
    return false;
  }
};

/**
 * Zone used when typing a clock: site if valid, else company, else UTC.
 *
 * @param {{siteTimezone?: string, companyTimezone?: string}} [places]
 * @return {string}
 */
export const resolvePlaceTimezone = ({ siteTimezone, companyTimezone } = {}) => {
  if (isValidTimeZone(siteTimezone)) {
    return siteTimezone;
  }
  if (isValidTimeZone(companyTimezone)) {
    return companyTimezone;
  }
  return "UTC";
};

/**
 * Show a stored UTC clock in a named zone (or the browser locale) with IST/GMT.
 *
 * @param {string|number|Date} datetime
 * @param {{locale?: string, timeZone?: string}} [options]
 * @return {string}
 */
export const formatClock = (datetime, { locale, timeZone } = {}) => {
  const options = { ...CLOCK_FORMAT };
  if (isValidTimeZone(timeZone)) {
    options.timeZone = timeZone;
  }
  return new Date(datetime).toLocaleString(locale, options);
};

const getDateTime = (datetime, options) => formatClock(datetime, options);

const WALL_CLOCK = "YYYY-MM-DD HH:mm";

/**
 * Short zone letters for a clock, e.g. IST, GMT, NZDT.
 *
 * @param {string} timeZone
 * @param {string|number|Date} [at]
 * @return {string}
 */
export const clockZoneAbbreviation = (timeZone, at = new Date()) => {
  const zone = isValidTimeZone(timeZone) ? timeZone : "UTC";
  const instant = at && typeof at.toDate === "function" ? at.toDate() : new Date(at);
  const parts = new Intl.DateTimeFormat("en", {
    timeZone: zone,
    timeZoneName: "short",
  }).formatToParts(Number.isNaN(instant.getTime()) ? new Date() : instant);
  return parts.find(part => part.type === "timeZoneName")?.value || zone;
};

/**
 * Store a picker wall clock as a UTC ISO instant in `timeZone`.
 *
 * @param {string|Date|import("dayjs").Dayjs|null|undefined} value
 * @param {string} timeZone
 * @return {string|null|undefined}
 */
export const clockToIsoInZone = (value, timeZone) => {
  if (value == null || value === "") {
    return value;
  }
  const zone = isValidTimeZone(timeZone) ? timeZone : "UTC";
  const wall = dayjs(value).format(WALL_CLOCK);
  return dayjs.tz(wall, WALL_CLOCK, zone).toISOString();
};

/**
 * Show a stored instant as a naive dayjs wall clock in `timeZone`.
 *
 * @param {string|Date|null|undefined} value
 * @param {string} timeZone
 * @return {import("dayjs").Dayjs|null}
 */
export const isoToClockDayjs = (value, timeZone) => {
  if (value == null || value === "") {
    return null;
  }
  const zone = isValidTimeZone(timeZone) ? timeZone : "UTC";
  return dayjs(dayjs(value).tz(zone).format(WALL_CLOCK), WALL_CLOCK);
};

/**
 * Calendar day of now (or `at`) in an IANA zone, not the laptop UTC date.
 *
 * @param {string} [timeZone]
 * @param {Date|string|number} [at]
 * @return {string}
 */
export const todayInTimeZone = (timeZone, at = new Date()) => {
  const zone = isValidTimeZone(timeZone) ? timeZone : "UTC";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(at));
  const lookup = type => parts.find(part => part.type === type)?.value;
  return `${lookup("year")}-${lookup("month")}-${lookup("day")}`;
};

const DATE_TIME_FORMAT = "MMM DD, YYYY HH:mm";

/**
 * Local noon on the given calendar day, or today when no value is passed.
 *
 * @param {string|Date|import("dayjs").Dayjs|null|undefined} value
 * @return {import("dayjs").Dayjs}
 */
export const middayLocal = value => {
  const d = value ? dayjs(value) : dayjs();
  return d.hour(12).minute(0).second(0).millisecond(0);
};

/**
 * ISO-style local datetime string for Formik and datetime APIs.
 *
 * @param {string|Date|import("dayjs").Dayjs|null|undefined} value
 * @return {string}
 */
export const middayLocalIso = value => middayLocal(value).format();

/**
 * Default header / line date when a PO form opens.
 *
 * @const {string}
 */
export const DEFAULT_PO_DATE = middayLocalIso();

/**
 * Keep an explicit clock time when the user set one; otherwise local noon
 * (date-only values typically arrive as 00:00).
 *
 * @param {string|Date|import("dayjs").Dayjs|null|undefined} value
 * @return {string}
 */
export const toPurchaseOrderDateTime = value => {
  if (!value) {
    return DEFAULT_PO_DATE;
  }
  const d = dayjs(value);
  if (d.hour() === 0 && d.minute() === 0 && d.second() === 0 && d.millisecond() === 0) {
    return middayLocal(d).format();
  }
  return d.format();
};

/**
 * Calendar day for date-only APIs. Uses the local `YYYY-MM-DD` prefix so
 * midday offsets (e.g. NZ noon) do not roll back through UTC.
 *
 * @param {string|Date|null|undefined} value
 * @return {string}
 */
export const toCalendarDate = value => {
  if (value == null || value === "") return "";
  if (typeof value.format === "function") {
    const formatted = value.format("YYYY-MM-DD");
    if (/^\d{4}-\d{2}-\d{2}$/.test(formatted)) return formatted;
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  const text = String(value);
  const separator = text.indexOf("T");
  return separator === -1 ? text.slice(0, 10) : text.slice(0, separator);
};

/**
 * True when both values are the same calendar day (or both empty).
 *
 * @param {string|Date|import("dayjs").Dayjs|null|undefined} left
 * @param {string|Date|import("dayjs").Dayjs|null|undefined} right
 * @return {boolean}
 */
export const sameCalendarDay = (left, right) => toCalendarDate(left) === toCalendarDate(right);

/**
 * Add whole days on a YYYY-MM-DD string. Does not use `new Date("YYYY-MM-DD")`,
 * which is UTC midnight and can jump the laptop calendar west of UTC.
 *
 * @param {string|Date|import("dayjs").Dayjs|null|undefined} value
 * @param {number} [days]
 * @return {string}
 */
export const addCalendarDays = (value, days) => {
  const day = toCalendarDate(value);
  if (!day) return "";
  const count = Number(days) || 0;
  return dayjs(day, "YYYY-MM-DD").add(count, "day").format("YYYY-MM-DD");
};

const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * True when `value` is already a YYYY-MM-DD day, not a clock.
 *
 * @param {*} value
 * @return {boolean}
 */
export const isCalendarDayString = value =>
  typeof value === "string" && CALENDAR_DAY.test(value.trim());

/**
 * List-cell text for a stored calendar day. Does not run the day through UTC.
 *
 * @param {string} value
 * @return {string}
 */
export const formatCalendarDay = value => {
  if (!isCalendarDayString(value)) return "";
  return dayjs(value.trim(), "YYYY-MM-DD").format("MMM DD, YYYY.");
};

/**
 * ISO clock bound for list filters that still query DateTimeFields.
 *
 * @param {Date|string} value
 * @param {{endOfDay?: boolean}} [options]
 * @return {string}
 */
export const formatClockFilterBound = (value, { endOfDay = false } = {}) => {
  const bound = endOfDay ? dayjs(value).endOf("day") : dayjs(value);
  return bound.format("YYYY-MM-DDTHH:mm:ssZ");
};

/**
 * Excel/list text for a stored calendar day or a clock.
 *
 * @param {*} value
 * @param {string} [dayFormat]
 * @param {string} [clockFormat]
 * @return {string}
 */
export const formatStoredDayOrClock = (
  value,
  dayFormat = "DD-MM-YYYY",
  clockFormat = "DD-MM-YYYY hh:mm A",
) => {
  if (value == null || value === "") return "";
  if (isCalendarDayString(value)) {
    return dayjs(value.trim(), "YYYY-MM-DD").format(dayFormat);
  }
  return dayjs(value).format(clockFormat);
};

/**
 * True when a list date-range filter is bound to a DateField (day) column.
 *
 * @param {string} [id]
 * @param {string} [title]
 * @return {boolean}
 */
export const usesCalendarDayFilterParams = (id, title) => {
  const field = String(id || "").toLowerCase();
  if (
    field === "eta" ||
    field === "estimated_date" ||
    field === "cargo_ready_date" ||
    field === "deadline_date"
  ) {
    return true;
  }
  const label = String(title || "").toUpperCase();
  return label === "ETA DATE" || label === "REQUIRED DATE" || label === "REQ DATE";
};

/**
 * Today's calendar day in an IANA zone, or the browser zone when omitted.
 *
 * @param {string} [timeZone]
 * @param {Date|string|number} [at]
 * @return {string}
 */
export const todayCalendarDate = (timeZone, at) =>
  todayInTimeZone(timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone, at);

/**
 * Ant DatePicker time panel. Midday default only when the caller asked for it.
 *
 * @param {boolean} defaultMiddayLocal
 * @return {{format: string, defaultValue?: import("dayjs").Dayjs}}
 */
export const datePickerShowTime = defaultMiddayLocal =>
  defaultMiddayLocal ? { format: "HH:mm", defaultValue: middayLocal() } : { format: "HH:mm" };

/**
 * Display format for datetime pickers with a time panel.
 *
 * @const {string}
 */
export const DATE_TIME_PICKER_FORMAT = DATE_TIME_FORMAT;

/**
 * Closed-field display for purchase order dates. The picker still edits the time.
 *
 * @const {string}
 */
export const DATE_ONLY_PICKER_FORMAT = "MMM DD, YYYY";

export { getTimeFromDate, getDateTime };
